<template>
	<GlassCard class="meal-calendar p-2">
		<h2 class="p-3">Monthly Meals</h2>
		<vue-cal
			hide-view-selector
			@cell-click="fetchMealsForDate"
			:disable-views="['years', 'year', 'week', 'day']"
			default-view="month"
			active-view="month"
			:startWeekOnSunday="true"
			style="height: 350px" />
		<div class="meal-details mt-3 container">
			<h5>Showing {{ formatToDay(selectedDate) }}</h5>
			<table class="table table-striped table-bordered table-sm">
				<thead>
					<tr>
						<th>Nutrient</th>
						<th>Amount</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>Total Calories</td>
						<td>{{ nutritionalSummary.totalCalories.toFixed(2) }}</td>
					</tr>
					<tr>
						<td>Total Protein</td>
						<td>{{ nutritionalSummary.totalProtein.toFixed(2) }} g</td>
					</tr>
					<tr>
						<td>Total Carbs</td>
						<td>{{ nutritionalSummary.totalCarbs.toFixed(2) }} g</td>
					</tr>
					<tr>
						<td>Total Fats</td>
						<td>{{ nutritionalSummary.totalFats.toFixed(2) }} g</td>
					</tr>
					<tr>
						<td>Total Sugars</td>
						<td>{{ nutritionalSummary.totalSugars.toFixed(2) }} g</td>
					</tr>
					<tr>
						<td>Total Fiber</td>
						<td>{{ nutritionalSummary.totalFiber.toFixed(2) }} g</td>
					</tr>
					<tr>
						<td>Total Sodium</td>
						<td>{{ nutritionalSummary.totalSodium.toFixed(2) }} mg</td>
					</tr>
					<tr>
						<td>Total Cholesterol</td>
						<td>{{ nutritionalSummary.totalCholesterol.toFixed(2) }} mg</td>
					</tr>
				</tbody>
			</table>
			<food-swiper :meals="meals" />
		</div>
	</GlassCard>
</template>

<script setup>
	import { ref, computed, onMounted, watch, onBeforeMount, onBeforeUnmount } from "vue"
	import GlassCard from "./GlassCard.vue"
	import VueCal from "vue-cal"
	import moment from "moment-timezone"
	import FoodSwiper from "@/components/FoodSwiper.vue"
	import { useTokenStore } from "@/stores/token"
	// import { hasTokenIssue, handleTokenIssue } from "@/utils/tokenIssue"
	import { fetchWithAuth } from "@/utils/api-client"
	// import { lastMealUpdated, lastMealDeleted } from "@/utils/mealBus"
	import EventBus from "@/utils/eventBus"
	import { isOnline } from "@/utils/networkBus"

	// const props = defineProps({ user: Object })
	const meals = ref([])
	const selectedDate = ref("")

	const tokenStore = useTokenStore()

	const fetchMealsForDate = async (date) => {
		if (!isOnline.value) return
		// Fetch meals for the selected date from your backend
		// This is pseudo-code, replace with your actual API call
		selectedDate.value = date
		try {
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const response = await fetchWithAuth(
				`/api/get-meals-for-date/${moment(date).unix()}`,
				{
					method: "GET",
					headers: {
						"User-Timezone": tz,
						// Authorization: `Bearer ${tokenStore.token}`,
					},
				}
			)

			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			if (!response.ok) throw new Error("Failed to fetch meals")
			meals.value = await response.json()
			// console.log(meals.value)
			// }
		} catch (error) {
			console.error("Error:", error)
		}
	}

	const nutritionalSummary = computed(() => {
		const summary = {
			totalCalories: 0,
			totalProtein: 0,
			totalCarbs: 0,
			totalFats: 0,
			totalSugars: 0,
			totalFiber: 0,
			totalSodium: 0,
			totalCholesterol: 0,
		}

		meals.value.forEach((meal) => {
			summary.totalCalories += parseFloat(meal.entry.nutritional_info.calories)
			summary.totalProtein += parseFloat(meal.entry.nutritional_info.protein)
			summary.totalCarbs += parseFloat(meal.entry.nutritional_info.carbs)
			summary.totalFats += parseFloat(meal.entry.nutritional_info.fats)
			summary.totalSugars += parseFloat(meal.entry.nutritional_info.sugars)
			summary.totalFiber += parseFloat(meal.entry.nutritional_info.fiber)
			summary.totalSodium += parseFloat(meal.entry.nutritional_info.sodium)
			summary.totalCholesterol += parseFloat(
				meal.entry.nutritional_info.cholesterol
			)
		})

		return summary
	})

	function formatToDay(timestamp) {
		return timestamp ? moment(timestamp).format("MM/DD/YYYY") : ""
	}

	onMounted(() => {
		fetchMealsForDate(moment().toDate()) // Fetch meals for today on component mount
	})

	// watch(lastMealUpdated, (v) => {
	// 	if (!v) return
	// 	//check to see if the new one that updated is our id and if it is, updated it
	// 	const index = meals.value.findIndex((a) => a.id === v.id)
	// 	if (index > -1) {
	// 		meals.value[index] = v
	// 	}
	// })

	// watch(lastMealDeleted, (v) => {
	// 	if (!v) return
	// 	//check to see if the new one that updated is our id and if it is, updated it
	// 	const index = meals.value.findIndex((a) => a.id === v.id)
	// 	if (index > -1) {
	// 		meals.value.splice(index, 1)
	// 	}
	// })

	let offMealUpdated = null
	let offMealDeleted = null

	onBeforeMount(() => {
		offMealUpdated = EventBus.on("meal-updated", (updatedMeal) => {
			if (!updatedMeal) return
			//check to see if the new one that updated is our id and if it is, updated it
			const index = meals.value.findIndex((a) => a.id === updatedMeal.id)
			if (index > -1) {
				meals.value[index] = updatedMeal
			}
		})

		offMealDeleted = EventBus.on("meal-deleted", (deletedMealId) => {
			if (!deletedMealId) return
			//check to see if the new one that updated is our id and if it is, updated it
			const index = meals.value.findIndex((a) => a.id === deletedMealId)
			if (index > -1) {
				meals.value.splice(index, 1)
			}
		})
	})

	onBeforeUnmount(() => {
		if (offMealUpdated) offMealUpdated()
		if (offMealDeleted) offMealDeleted()
	})
</script>

<style lang="less" scoped>
	.vuecal__cell--out-of-scope {
		color: white;
	}

	.meal-thumbnail {
		width: 100%;
		height: 150px; /* Adjust as necessary */
		object-fit: cover;
	}

	.meal-calendar {
		/* Allow the glass-card to handle background; add spacing tweaks */
		border-radius: 12px;
	}
</style>
